import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, catchError, filter, from, switchMap, take, throwError } from 'rxjs';
import { AuthStorageService } from '../services/auth-storage.service';
import { RefreshTokenUseCaseService } from '@core/use-cases/auth/refresh-token.use-case.service';
import { getDeviceInfo } from '@shared/helpers/get-device-info.helper';
import { IAuthData } from '../../modules/auth/interfaces/auth-data.interface';
import { isTokenExpiredWithGracePeriod } from '@shared/helpers/is-token-expired';
import { environment } from '../../../environments/environment';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

// Auth-exempt endpoints - specific paths that don't require authentication
const authExemptEndpoints = new Set([
  `${environment.baseUrl}/auth/login`,
  `${environment.baseUrl}/auth/refresh-token`,
]);

const pathExceptions = ['assets/', 'favicon.ico'];

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authStorage = inject(AuthStorageService);
  const refreshTokenUseCase = inject(RefreshTokenUseCaseService);
  const router = inject(Router);

  const authData = authStorage.getAuthData();

  // Check if the request URL is in the auth-exempt list
  if (authExemptEndpoints.has(req.url) || pathExceptions.some(path => req.url.includes(path))) {
    return next(req);
  }

  if (!authData) {
    return next(req);
  }

  const isExpired = isTokenExpiredWithGracePeriod(new Date(authData.expiresAt));

  function addAuthHeader(token: string) {
    const reqWithAuth = req.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
      },
    });
    return reqWithAuth;
  }

  function handle401OrExpiredToken(authData: IAuthData) {
    if (!authData.refreshToken) {
      authStorage.clearAuthData();
      router.navigate(['/login']).then(() => {});
      return throwError(() => new Error('Refresh token ausente'));
    }

    if (!isRefreshing) {
      isRefreshing = true;
      refreshTokenSubject.next(null);

      return from(
        refreshTokenUseCase.execute({
          refreshToken: authData.refreshToken,
          deviceInfo: getDeviceInfo(),
        })
      ).pipe(
        switchMap(newAuth => {
          isRefreshing = false;
          refreshTokenSubject.next(newAuth.accessToken);
          // Ensure we add the auth header before forwarding the request
          return next(addAuthHeader(newAuth.accessToken));
        }),
        catchError(err => {
          console.error(err);
          isRefreshing = false;
          authStorage.clearAuthData();
          router.navigate(['/login']).then(() => {});
          return throwError(() => err);
        })
      );
    }

    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => {
        return next(addAuthHeader(token));
      })
    );
  }

  if (isExpired) {
    return handle401OrExpiredToken(authData);
  }

  const reqWithAuth = addAuthHeader(authData.accessToken);

  return next(reqWithAuth).pipe(
    catchError(error => {
      if (error.status === 401) {
        return handle401OrExpiredToken(authData);
      }
      return throwError(() => error);
    })
  );
};
